import { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { X } from 'lucide-react';
import SEO from '@/components/SEO';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  // Updated to reference files in public/images/
  const galleryImages = [
    // Existing curated images
    { url: '/images/bungee.webp', title: 'Victoria Falls Bridge Bungee', category: 'Adventure' },
    { url: '/images/rafting.webp', title: 'White Water Rafting', category: 'Adventure' },
    { url: '/images/sunset_cruise.webp', title: 'Zambezi Sunset Cruise', category: 'River Safaris' },
    { url: '/images/walking_safari.webp', title: 'Walking Safari Experience', category: 'Wildlife' },

    // Additional Available Local Images
    { url: '/images/south-luangwa-national-park-zambia.jpg', title: 'South Luangwa National Park', category: 'Safari' },
    { url: '/images/Bridge-Swing-4-scaled.jpg', title: 'Bridge Swing Action', category: 'Adventure' },
    { url: '/images/safari.webp', title: 'African Safari Sunset', category: 'Landscapes' },
    { url: '/images/Historical tour1.jpg', title: 'Historic Bridge Construction', category: 'Culture' },
    { url: '/images/Historical tour2.jpg', title: 'Bridge History Tour', category: 'Culture' },
    { url: '/images/528-2023-5281.webp', title: 'River Adventure', category: 'Adventure' },
    { url: '/images/528-2023-6151.webp', title: 'Wildlife Encounter', category: 'Wildlife' },

    // From Destinations
    { url: 'https://images.pexels.com/photos/1637146/pexels-photo-1637146.jpeg', title: 'Victoria Falls', category: 'Landmarks' },
    { url: 'https://images.pexels.com/photos/1670732/pexels-photo-1670732.jpeg', title: 'South Luangwa Leopard', category: 'Wildlife' },
    { url: 'https://images.pexels.com/photos/59989/elephant-herd-of-elephants-african-bush-elephant-africa-59989.jpeg', title: 'Chobe Elephants', category: 'Wildlife' },
    { url: 'https://images.pexels.com/photos/695214/pexels-photo-695214.jpeg', title: 'Mana Pools Canoe', category: 'Safari' },
    { url: 'https://images.pexels.com/photos/4577792/pexels-photo-4577792.jpeg', title: 'Maasai Mara Migration', category: 'Wildlife' },

    // From Activities
    { url: '/images/8-6.jpg', title: 'Bungee Jumping', category: 'Adventure' },
    { url: '/images/2.jpg', title: 'Bridge Swing', category: 'Adventure' },
    { url: '/images/the-victoria-falls-gorge-swing-1-1024x464.jpg', title: 'Gorge Swing', category: 'Adventure' },
    { url: '/images/Bridge-Slide-7-scaled.jpg', title: 'Zip Line', category: 'Adventure' },
    { url: '/images/5.jpg', title: 'Bridge Slide', category: 'Adventure' },
    { url: '/images/Heli-Shoot-710256.jpg', title: 'Flight of Angels', category: 'Scenic Flights' },
    { url: '/images/Heli-Flight4.jpg', title: 'Zambezi Spectacular', category: 'Scenic Flights' },
    { url: '/images/whitewater-rafting-images-10.jpg', title: 'Zambezi Rafting', category: 'Adventure' },
    { url: '/images/DSC01108.jpg', title: 'Adventure Jetboat', category: 'Adventure' },
    { url: '/images/Riversong-2-10.jpg', title: 'Riversong Luxury Cruise', category: 'River Safaris' },
    { url: 'https://images.pexels.com/photos/35383072/pexels-photo-35383072.jpeg', title: 'Hwange Safari', category: 'Safari' },
    { url: '/images/6-11.jpg', title: 'Elephant Interaction', category: 'Wildlife' },
    { url: '/images/Simunye-Spirit-Of-Africa-31.jpg', title: 'Simunye Theatre', category: 'Culture' },
    { url: '/images/screenshot-from-2024-06-18-16-21-43.b7f683f9.png', title: 'Bushtracks Express', category: 'Dining' },
    { url: '/images/Bridge-Tour-1-scaled.jpg', title: 'Historical Bridge Tour', category: 'Culture' },
    { url: '/images/Traditional-Village-tour-3-scaled.jpg', title: 'Village Visit', category: 'Culture' },
    { url: '/images/Royal-Livingstone-Express.jpg', title: 'Royal Livingstone Express', category: 'Dining' },
    { url: '/images/Microlight-flight-safari-on-Victoria-Falls-1024x409.jpg', title: 'Microlight Flight', category: 'Scenic Flights' },
    { url: '/images/uac_2.jpg', title: 'Helicopter Flight Zambia', category: 'Scenic Flights' },
    { url: '/images/the_royal_livingstone_kubu_960x519.jpg', title: 'Livingstone Island Lunch', category: 'Dining' },
    { url: '/images/c0.jpg', title: 'Livingstone Island', category: 'River Safaris' },
    { url: '/images/Elephant_Cafe_Exterior.webp', title: 'The Elephant Cafe', category: 'Dining' },
    { url: '/images/caption.jpg', title: 'Rhino Walking Safari', category: 'Wildlife' },
    { url: '/images/livingstone-quad-biking-60-minute-eco-trail-1488051-1024x684.jpg', title: 'Quad Biking', category: 'Adventure' },
    { url: '/images/approaching-the-african.jpg', title: 'African Queen Cruise', category: 'River Safaris' },
    { url: '/images/8-2.jpg', title: 'Victoria Falls Tour', category: 'Landmarks' },
    { url: '/images/bridge-tour-9.jpg', title: 'Bridge Tour', category: 'Culture' },
    { url: '/images/drifting down the zambezi compressed.webp', title: 'Zambezi River Float', category: 'River Safaris' },
    { url: '/images/bigstock_Botswana_Chobe_River_African_Elephants_Safari_Cruise-1200x704.jpg', title: 'Chobe River Cruise', category: 'River Safaris' },
    { url: '/images/80.jpg', title: 'Chobe Game Drive', category: 'Safari' }
  ];

  const categories = ['All', ...new Set(galleryImages.map(img => img.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <div className="min-h-screen">
      <SEO
        title="Gallery"
        description="Browse our stunning collection of safari photos. Wildlife, landscapes, and happy travelers in Zimbabwe, Zambia, and Botswana."
        keywords="African Safari Photos, Wildlife Photography, Victoria Falls Images, Safari Gallery"
        canonicalUrl="https://www.peregrinetoursandsafaris.com/gallery"
        schema={{
          "@context": "https://schema.org",
          "@type": "ImageGallery",
          "name": "African Safari Gallery",
          "description": "Visual highlights from our tours and safaris across Southern Africa.",
          "image": galleryImages.slice(0, 10).map(img => ({
            "@type": "ImageObject",
            "url": img.url.startsWith('/')
              ? `https://www.peregrinetoursandsafaris.com${img.url}`
              : img.url,
            "caption": img.title
          }))
        }}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto mb-12">
            <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
              Gallery
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-serif leading-tight">
              Moments Captured in the Wild
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Experience the beauty of Africa through our lens. From breathtaking landscapes to intimate wildlife encounters, every image tells a story of adventure.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-background border-y border-border">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <Card
                key={index}
                className="group cursor-pointer overflow-hidden border-2 hover:border-primary/30 transition-all duration-300"
                onClick={() => setSelectedImage(image)}
              >
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-background font-semibold text-sm mb-1">{image.title}</h3>
                    <span className="text-xs text-background/80">{image.category}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-foreground/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 w-12 h-12 bg-background/10 hover:bg-background/20 rounded-full flex items-center justify-center transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-6 h-6 text-background" />
          </button>
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.url}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center">
              <h3 className="text-2xl font-bold text-background font-serif mb-2">
                {selectedImage.title}
              </h3>
              <span className="inline-block px-4 py-1 bg-primary rounded-full text-primary-foreground text-sm">
                {selectedImage.category}
              </span>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;